import { Icon, type IconProps } from './Icon';

/**
 * Navigation and interface glyphs.
 *
 * Navigation icons mirror the eight main sections from the GDD wireframe
 * (Übersicht, Gebäude, Forschung, Werft, Abwehr, Sektor-Karte, Händler, Casino);
 * the rest are the interaction primitives the HUD needs.
 */

/* ---------------------------- Navigation ---------------------------- */

/** Übersicht — command dashboard. */
export function OverviewIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="4.5" rx="1.5" />
      <rect x="13.5" y="10.5" width="7.5" height="10.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </Icon>
  );
}

/** Gebäude — planetary structures. */
export function BuildingIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 21h18" />
      <path d="M5 21V10l4-3 4 3v11" />
      <path d="M13 21v-7h6v7" />
      <path d="M8 13h2M8 17h2M16 17.5h1" opacity={0.7} />
    </Icon>
  );
}

/** Forschung — quantum research lab. */
export function ResearchIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
      <ellipse cx="12" cy="12" rx="9" ry="4" />
      <ellipse cx="12" cy="12" rx="9" ry="4" transform="rotate(60 12 12)" />
      <ellipse cx="12" cy="12" rx="9" ry="4" transform="rotate(120 12 12)" />
    </Icon>
  );
}

/** Werft — shipyard. */
export function ShipyardIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2.5c2.6 2.6 4 6 4 9.5l-1.5 4h-5L8 12c0-3.5 1.4-6.9 4-9.5Z" />
      <path d="M8 13.5 4.5 16v3l3.8-1.8M16 13.5 19.5 16v3l-3.8-1.8" />
      <path d="M11 20h2" />
      <circle cx="12" cy="9.5" r="1.4" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/** Abwehr — planetary defence. */
export function DefenseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2.5 20 6v6.2c0 4.6-3.2 8-8 9.3-4.8-1.3-8-4.7-8-9.3V6l8-3.5Z" />
      <path d="M12 7.5v9" opacity={0.6} />
      <path d="M8.5 11h7" opacity={0.6} />
    </Icon>
  );
}

/** Sektor-Karte — galactic ring map. */
export function SectorMapIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5.5" opacity={0.65} />
      <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
      <circle cx="12" cy="3" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="17.5" cy="15.5" r="1.1" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/** Händler — trade relay. */
export function TradeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 8.5h13l-3-3M20.5 15.5h-13l3 3" />
      <circle cx="19" cy="8.5" r="1.6" />
      <circle cx="5" cy="15.5" r="1.6" />
    </Icon>
  );
}

/** Shop — Chronos-Kristall-Käufe und Bundles. */
export function ShopIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6.5 8h11l-1 12.2a1.8 1.8 0 0 1-1.8 1.6H9.3a1.8 1.8 0 0 1-1.8-1.6L6.5 8Z" />
      <path d="M9 8V6.5a3 3 0 0 1 6 0V8" />
      <path d="M9.5 11.5v1.8M14.5 11.5v1.8" opacity={0.7} />
    </Icon>
  );
}

/** Casino — credit sink. */
export function CasinoIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M9 6v12M15 6v12" opacity={0.5} />
      <circle cx="6" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="18" cy="12" r="1.2" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/* ---------------------------- Game objects ---------------------------- */

/** Planet. */
export function PlanetIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="7" />
      <path d="M5.2 14.5c4 1.6 9.6 1.6 13.6 0" opacity={0.6} />
      <ellipse cx="12" cy="12" rx="11" ry="3.4" transform="rotate(-22 12 12)" opacity={0.75} />
    </Icon>
  );
}

/** Mond — moon base. */
export function MoonIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="7.5" />
      <circle cx="9.5" cy="9.5" r="1.6" opacity={0.6} />
      <circle cx="14.5" cy="14" r="2.2" opacity={0.6} />
      <circle cx="15" cy="8.5" r="1" opacity={0.6} />
    </Icon>
  );
}

/** Flotte — fleet in transit. */
export function FleetIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M13 3.5c2.2 2.4 3.4 5.2 3.4 8.4L15 16h-4l-1.4-4.1c0-3.2 1.2-6 3.4-8.4Z" />
      <path d="m9.6 12-3.6 2v2.6l3-1.4M16.4 12l3.6 2v2.6l-3-1.4" />
      <path d="M11.5 19.5h3" opacity={0.7} />
      <path d="M4 4.5 6 6M4 9l1.6.8" opacity={0.5} />
    </Icon>
  );
}

/** Asteroid / Trümmerfeld. */
export function AsteroidIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6.5 6 12 3.5l5.5 3 1.5 6-4 5-6.5.5-4-4.5L6.5 6Z" />
      <circle cx="10.5" cy="9.5" r="1.3" opacity={0.6} />
      <circle cx="14.5" cy="13" r="1.8" opacity={0.6} />
    </Icon>
  );
}

/** Komet — the incoming-threat glyph. */
export function CometIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="16.5" cy="7.5" r="3.5" />
      <path d="M13.8 9.8 4 19.5" />
      <path d="M12 8.5 6.5 11M15.5 12.5 13 18" opacity={0.65} />
    </Icon>
  );
}

/** Phalanx / radar scan. */
export function RadarIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 21a9 9 0 1 1 9-9" />
      <path d="M12 16.5a4.5 4.5 0 0 1 4.5-4.5" opacity={0.7} />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <path d="M12 12 20 6" />
    </Icon>
  );
}

/* ---------------------------- Interaction ---------------------------- */

/** Beschleunigen — the Chronos speed-up action. */
export function BoostIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M13.5 2.5 5 13.5h5.5L9.5 21.5 19 10.5h-5.8l.3-8Z" />
    </Icon>
  );
}

export function ClockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </Icon>
  );
}

export function AlertIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3.5 21.5 20H2.5L12 3.5Z" />
      <path d="M12 9.5v4.5" />
      <circle cx="12" cy="17" r="0.9" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/** Settings — the gear in the header, not a nav-dock tab (see Switch/SettingsView docs). */
export function SettingsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3.5v2.3M12 18.2v2.3M20.5 12h-2.3M5.8 12H3.5M17.8 6.2l-1.6 1.6M7.8 16.2l-1.6 1.6M17.8 17.8l-1.6-1.6M7.8 7.8 6.2 6.2" />
    </Icon>
  );
}

export function InfoIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.5" />
      <circle cx="12" cy="7.8" r="0.9" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/** Help center / tutorial entry point — a question mark in the same ring badge as InfoIcon. */
export function HelpIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.3 9.4a2.7 2.7 0 0 1 5.2.9c0 1.7-2.5 2-2.5 3.7" />
      <circle cx="12" cy="16.6" r="0.9" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/** Guided-tour compass — orientation, "show me around". */
export function CompassIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m14.8 9.2-2 4.2-4.2 2 2-4.2 4.2-2Z" />
    </Icon>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m4.5 12.5 5 5 10-11" />
    </Icon>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Icon>
  );
}

export function LockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4.5" y="10" width="15" height="10.5" rx="2" />
      <path d="M8 10V7.5a4 4 0 0 1 8 0V10" />
      <circle cx="12" cy="15.2" r="1.2" fill="currentColor" stroke="none" />
    </Icon>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m9 5 7 7-7 7" />
    </Icon>
  );
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m5 9 7 7 7-7" />
    </Icon>
  );
}

export function PlusIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 5v14M5 12h14" />
    </Icon>
  );
}

export function ArrowUpIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 19V5M6 11l6-6 6 6" />
    </Icon>
  );
}

/** Level / XP — commander rank chevrons. */
export function RankIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m5 13 7-7 7 7" />
      <path d="m5 19 7-7 7 7" opacity={0.6} />
    </Icon>
  );
}

/** Allianz — alliance insignia. */
export function AllianceIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2.5 20.5 7v10L12 21.5 3.5 17V7L12 2.5Z" />
      <path d="M12 7.5 16 10v4.5L12 17l-4-2.5V10l4-2.5Z" opacity={0.65} />
    </Icon>
  );
}

/** Cloud-Speicherung — sync status. */
export function CloudIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M7 18a4.5 4.5 0 0 1-.5-8.98 5.5 5.5 0 0 1 10.6-1.44A4 4 0 0 1 17 18H7Z" />
    </Icon>
  );
}
